/**
 * Emergency Security API Service
 * Provides emergency security controls for admin users only.
 */
import BaseAPIService from './baseAPI';
import type { 
  EmergencyStatus, 
  EmergencyActionRequest, 
  EmergencyActionResponse, 
  EmergencyLog, 
  SecurityIncident 
} from '../types/emergency';

// Emergency Types
export interface EmergencySettings {
  id: string;
  is_emergency_active: boolean;
  emergency_level: 'normal' | 'warning' | 'critical' | 'lockdown';
  emergency_message?: string;
  block_registration: boolean;
  block_login: boolean;
  block_all_api: boolean;
  block_post_creation: boolean;
  block_content_generation: boolean;
  block_social_posting: boolean;
  block_file_uploads: boolean;
  enable_maintenance_mode: boolean;
  blocked_user_ids: string[];
  blocked_ip_addresses: string[];
  allowed_admin_ips: string[];
  emergency_rate_limit_enabled: boolean;
  emergency_rate_limit_requests: number;
  emergency_rate_limit_window: number;
  maintenance_title?: string;
  maintenance_message?: string;
  maintenance_estimated_duration?: number;
  created_at: string;
  updated_at: string;
}

export interface BlockUserRequest {
  user_id: string;
  reason: string;
  admin_note?: string;
}

export interface BlockIPRequest {
  ip_address: string;
  reason: string;
  admin_note?: string;
}

export interface UnblockRequest {
  reason: string;
  admin_note?: string;
}

export interface PaginatedLogsResponse {
  logs: EmergencyLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface PaginatedIncidentsResponse {
  incidents: SecurityIncident[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class EmergencyAPI extends BaseAPIService {
  private readonly basePath = '/admin/emergency';

  /**
   * Public emergency banner status (no admin auth).
   */
  async getPublicStatus(): Promise<Pick<EmergencyStatus, 'is_emergency_active' | 'emergency_level' | 'emergency_message' | 'maintenance_mode'>> {
    return this.get('/emergency/public-status');
  }

  /**
   * Get current emergency status (admin only).
   */
  async getStatus(): Promise<EmergencyStatus> {
    return this.get<EmergencyStatus>(`${this.basePath}/status`);
  }

  /**
   * Block all API access (admin users exempted)
   */
  async blockAllAPI(request: EmergencyActionRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/actions/block-all-api`,
      request
    );
  }

  /**
   * Enable maintenance mode
   */
  async enableMaintenance(request: EmergencyActionRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/actions/enable-maintenance`,
      request
    );
  }

  /**
   * Block user registration
   */
  async blockRegistration(request: EmergencyActionRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/actions/block-registration`,
      request
    );
  }

  /**
   * Reset all emergency blocks
   */
  async resetAll(request: EmergencyActionRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/actions/reset-all`,
      request
    );
  }

  /**
   * Block a specific user
   */
  async blockUser(request: BlockUserRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/users/block`,
      request
    );
  }

  /**
   * Unblock a specific user
   */
  async unblockUser(userId: string, request: UnblockRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/users/${userId}/unblock`,
      request
    );
  }

  /**
   * Block a specific IP address
   */
  async blockIP(request: BlockIPRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/ips/block`,
      request
    );
  }

  /**
   * Unblock a specific IP address
   */
  async unblockIP(ipAddress: string, request: UnblockRequest): Promise<EmergencyActionResponse> {
    return this.post<EmergencyActionResponse>(
      `${this.basePath}/ips/${ipAddress}/unblock`,
      request
    );
  }

  /**
   * Get emergency action logs with pagination
   */
  async getLogs(params?: {
    page?: number;
    limit?: number;
    action?: string;
  }): Promise<PaginatedLogsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.action) searchParams.append('action', params.action);

    const url = `${this.basePath}/logs${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    return this.get<PaginatedLogsResponse>(url);
  }

  /**
   * Get security incidents with pagination
   */
  async getIncidents(params?: {
    page?: number;
    limit?: number;
    severity?: string;
    resolved?: boolean;
  }): Promise<PaginatedIncidentsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.severity) searchParams.append('severity', params.severity);
    if (params?.resolved !== undefined) searchParams.append('resolved', params.resolved.toString());

    const url = `${this.basePath}/incidents${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    return this.get<PaginatedIncidentsResponse>(url);
  }

  /**
   * Get users list for blocking interface
   */
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{
    users: Array<{
      id: string;
      email: string;
      username?: string;
      full_name?: string;
      is_active: boolean;
      is_superuser: boolean;
      created_at: string;
      last_login?: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);

    const url = `${this.basePath}/users${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    return this.get(url);
  }

  /**
   * Check if emergency mode is active
   */
  async isEmergencyActive(): Promise<boolean> {
    try {
      const status = await this.getStatus();
      return status.is_emergency_active;
    } catch (error) {
      console.error('Error checking emergency status:', error);
      return false;
    }
  }

  /**
   * Get emergency level
   */
  async getEmergencyLevel(): Promise<string> {
    try {
      const status = await this.getStatus();
      return status.emergency_level;
    } catch (error) {
      console.error('Error getting emergency level:', error);
      return 'normal';
    }
  }

  /**
   * Get blocked users count
   */
  async getBlockedUsersCount(): Promise<number> {
    try {
      const status = await this.getStatus();
      return status.blocked_users_count;
    } catch (error) {
      console.error('Error getting blocked users count:', error);
      return 0;
    }
  }

  /**
   * Get blocked IPs count
   */
  async getBlockedIPsCount(): Promise<number> {
    try {
      const status = await this.getStatus();
      return status.blocked_ips_count;
    } catch (error) {
      console.error('Error getting blocked IPs count:', error);
      return 0;
    }
  }
}

export const emergencyAPI = new EmergencyAPI();
export default emergencyAPI;

// Re-export types for convenience
export type { EmergencyStatus, EmergencyActionRequest, EmergencyActionResponse, EmergencyLog, SecurityIncident } from '../types/emergency'; 